import { type ReactElement, type ReactNode, useId } from 'react';
import { useMyOrdersQuery } from '@/entities/order';
import { useReviewStatesForOrdersQuery } from '@/entities/review';
import { formatCount } from '@/shared/lib';
import { Button, Pagination } from '@/shared/ui';
import { MyOrderEmpty } from './my-order-empty';
import { MyOrderListSkeleton } from './my-order-list-skeleton';
import { MyOrderTable } from './my-order-table';

export interface MyOrderListProps {
  readonly page: number;
  /** 페이지 이동 — 페이지가 URL 쿼리(page)를 바꾼다. */
  readonly onPageChange: (page: number) => void;
}

/** 오류·범위 밖 안내 — 내 견적 목록과 같은 얇은 위아래 선 사이의 가운데 정렬 칸. */
const NOTICE_CLASS =
  'flex flex-col items-center gap-4 border-y border-foreground/10 py-16 text-center';

/**
 * 내 주문 목록(주문 설계 §8-2) — 내 견적 목록과 같은 짜임(머리·전체 건수·표·페이지 이동).
 * 주문 번호·상품명·수량·주문일·상태(주문 고객 화면 이름)·주문 금액을 서버 순서(최신순)대로 보여 준다.
 * 페이지를 바꾸는 동안에는 이전 페이지를 그대로 두고(keepPreviousData), 첫 로딩만 자리표시 줄을 보인다.
 */
export const MyOrderList = ({ page, onPageChange }: MyOrderListProps): ReactElement => {
  const query = useMyOrdersQuery({ page });
  const headingId = useId();
  const reviewStates = useReviewStatesForOrdersQuery(
    query.data?.items.map((order) => order.id) ?? [],
  );

  const content = ((): ReactNode => {
    if (query.isPending) {
      return <MyOrderListSkeleton />;
    }

    if (query.isError) {
      return (
        <div role="alert" className={NOTICE_CLASS}>
          <p className="text-sm text-destructive">주문 목록을 불러오지 못했습니다.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void query.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      );
    }

    const { items, meta } = query.data;
    const lastPage = Math.max(1, meta.totalPages);

    // 쪽을 옮기는 동안 자리표시자는 이전 쪽이다. 그 쪽이 비어 있었으면(범위 밖 → 마지막 쪽 등) 새 쪽 기준으로 "주문 없음"·
    // "범위 밖"을 판정하면 틀린 안내가 잠깐 보이므로, 새 쪽이 올 때까지 자리표시 줄을 보인다.
    if (query.isPlaceholderData && items.length === 0) {
      return <MyOrderListSkeleton />;
    }

    // 주문은 있는데 주소의 쪽이 범위를 벗어나 비었으면 "주문 없음"이 아니라 범위 밖이다(내 견적 목록과 같은 규칙).
    if (items.length === 0 && meta.total > 0 && page > meta.totalPages) {
      return (
        <div className={NOTICE_CLASS}>
          <p role="status" className="text-sm text-muted-foreground">
            페이지 범위를 벗어났습니다.
          </p>
          <Button variant="outline" size="sm" onClick={() => onPageChange(lastPage)}>
            마지막 페이지로
          </Button>
        </div>
      );
    }

    if (items.length === 0) {
      return <MyOrderEmpty />;
    }

    return (
      <div className="flex flex-col gap-10">
        <MyOrderTable rows={items} reviewStates={reviewStates.data} />
        <Pagination
          page={page}
          totalPages={meta.totalPages}
          onPageChange={onPageChange}
          className="justify-center"
        />
      </div>
    );
  })();

  const total = query.data?.meta.total;

  return (
    <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2
            id={headingId}
            className="text-2xl leading-tight font-bold tracking-[-0.02em] sm:text-[1.75rem]"
          >
            내 주문
          </h2>
          <p className="text-sm text-muted-foreground">
            주문한 상품의 결제·제작·배송 진행과 주문 금액을 확인합니다.
          </p>
        </div>
        {total !== undefined && total > 0 && !query.isError ? (
          <p className="text-sm text-muted-foreground">
            전체{' '}
            <span className="font-semibold text-foreground tabular-nums">{formatCount(total)}</span>
            건
          </p>
        ) : null}
      </div>
      {content}
    </section>
  );
};
